import { TopNav, PageShell, BackCoverFooter, BrutalistBadge } from "@/app/components/brand-scaffold";
import { siteHeaderActions, siteNavItems } from "@/app/content";

export default function TrustSettlementPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />
      <main className="relative z-10 w-full overflow-hidden px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8">
            <BrutalistBadge bgClass="bg-[#A3E635]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">
              TRUST & SETTLEMENT
            </BrutalistBadge>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-black mb-4">
            信任与结算
          </h1>
          <p className="text-lg font-bold text-zinc-600 mb-12">
            调用如何被计量、审计和保护。
          </p>

          <div className="space-y-12">
            {/* Section 1: Current Status */}
            <section>
              <h2 className="text-2xl font-black text-black mb-4 border-b-4 border-black pb-2">当前状态</h2>
              <div className="border-4 border-black bg-[#FACC15]/20 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2"><span className="font-black text-black">•</span> Preview 期间所有调用免费，Call Credit 仅用于内部计量。</li>
                  <li className="flex items-start gap-2"><span className="font-black text-black">•</span> Call Credit 不是法币余额，当前没有提现能力。</li>
                  <li className="flex items-start gap-2"><span className="font-black text-black">•</span> 所有调用记录可审计，result_package 包含完整执行元数据。</li>
                </ul>
              </div>
            </section>

            {/* Section 2: How Calls Are Protected */}
            <section>
              <h2 className="text-2xl font-black text-black mb-4 border-b-4 border-black pb-2">调用双方如何被保护</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="border-4 border-black bg-white/85 p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <h3 className="font-black mb-2">Caller 侧</h3>
                  <ul className="space-y-2 text-sm text-zinc-700">
                    <li>• 调用前可查看 contract 全部字段</li>
                    <li>• result_package 包含输出、耗时、状态码</li>
                    <li>• 失败调用有标准化错误码和原因</li>
                    <li>• 本地 supervisor 记录所有调用日志</li>
                  </ul>
                </div>
                <div className="border-4 border-black bg-white/85 p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <h3 className="font-black mb-2">Responder 侧</h3>
                  <ul className="space-y-2 text-sm text-zinc-700">
                    <li>• 核心实现不出本地边界</li>
                    <li>• 协议层 token 验证调用方身份</li>
                    <li>• 版本化契约保护向后兼容</li>
                    <li>• 调用量和模式可本地监控</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3: result_package and Metering */}
            <section>
              <h2 className="text-2xl font-black text-black mb-4 border-b-4 border-black pb-2">result_package 如何支持计量</h2>
              <div className="border-4 border-black bg-white/85 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <p className="text-sm text-zinc-700 mb-4">每次调用返回的 result_package 包含：</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {resultPackageFields.map((f) => (
                    <div key={f.field} className="border-2 border-black/20 p-3">
                      <code className="text-xs font-mono font-bold">{f.field}</code>
                      <p className="text-xs text-zinc-600 mt-1">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Section 4: Commercialization Principles */}
            <section>
              <h2 className="text-2xl font-black text-black mb-4 border-b-4 border-black pb-2">商业化原则</h2>
              <div className="border-4 border-black bg-[#A3E635]/10 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <ul className="space-y-4 text-sm text-zinc-800">
                  <li className="flex items-start gap-3">
                    <span className="font-black text-[#A3E635] text-lg">1</span>
                    <span>正式商业化会在 Marketplace 出现稳定供需后再公告。</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="font-black text-[#A3E635] text-lg">2</span>
                    <span>Preview 期不承诺抽成比例，不开放法币提现。</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="font-black text-[#A3E635] text-lg">3</span>
                    <span>协议层计量字段已就绪，商业化启动时无需改动 Responder 代码。</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="font-black text-[#A3E635] text-lg">4</span>
                    <span>Local Mode 和 Self-host 部署永远免费使用，不受商业化影响。</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5: Future Pricing */}
            <section>
              <h2 className="text-2xl font-black text-black mb-4 border-b-4 border-black pb-2">未来定价</h2>
              <p className="text-sm text-zinc-600">
                定价结构将在 Marketplace 出现真实活跃供需后另行公告。当前阶段的重点是让协议和本地运行时稳定可用。
              </p>
            </section>
          </div>
        </div>
      </main>
      <BackCoverFooter />
    </PageShell>
  );
}

const resultPackageFields = [
  { field: "status", desc: "调用成功/失败/超时" },
  { field: "duration_ms", desc: "执行耗时" },
  { field: "output", desc: "结构化输出" },
  { field: "cost_tokens", desc: "消耗 token 数" },
  { field: "version", desc: "Hotline 版本号" },
  { field: "timestamp", desc: "调用时间戳" },
];
